"use client";

import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    Link,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import Cookies from "js-cookie";
import { getApiUrl } from "@/utils/api";
import { LicenseAgreement } from "./LicenseAgreement";
import { fetcher } from "@/utils/fetch";
import { Typography } from "@/components/wrappers/Typography";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Logo from "./branding/Logo";
import { useIsVariant } from "@/hooks/useVariant";
import { useTranslation } from "react-i18next";
import { LocaleSelector } from "./LocaleSelector";

const LoginPage = () => {
    const isATD = useIsVariant("atd");
    const isCada = useIsVariant("cada");
    const { t } = useTranslation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = React.useState(false);
    const [tempToken, setTempToken] = useState("");

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const [showTermsDialog, setShowTermsDialog] = useState(false);

    const cancelTerms = () => {
        setShowTermsDialog(false);
    };

    const acceptTerms = async () => {
        setShowTermsDialog(false);

        // Accept terms
        const response = await fetcher(
            getApiUrl("/accept_terms"),
            "GET",
            {
                "Content-Type": "application/json",
                Authorization: `Bearer ${tempToken}`,
            },
            null,
            false,
            { redirectOnUnauthorized: false }
        );
        if (response) {
            // Set jwt from temp token held on from the login call
            Cookies.set("esiq-nada-jwt", tempToken, {
                expires: new Date(response.expires_at),
            });
            // Reload the current page to stay on the original URL the user was trying to access
            window.location.reload();
        }
    };

    const login = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetcher(
                getApiUrl("/auth"),
                "POST",
                { "Content-Type": "application/json" },
                JSON.stringify({ username: email, password }),
                false,
                { redirectOnUnauthorized: false }
            );

            if (response.access_token) {
                // Delay setting cookie in order to check license agreement acceptance
                // Prevents users from bypassing the license agreement
                setTempToken(response.access_token);
                const userInfoResponse = await fetcher(
                    getApiUrl("/user_info"),
                    "GET",
                    {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${response.access_token}`,
                    },
                    null,
                    false,
                    { redirectOnUnauthorized: false }
                );

                // Only show if terms have not been accepted
                if (userInfoResponse && !userInfoResponse.terms_accepted) {
                    setShowTermsDialog(true);
                } else {
                    // Login as normal if terms have been accepted
                    Cookies.set("esiq-nada-jwt", response.access_token, {
                        expires: new Date(response.expires_at),
                    });
                    // Reload the current page to stay on the original URL the user was trying to access
                    window.location.reload();
                }
            } else {
                throw new Error("Login successful, but no token received.");
            }
        } catch (err) {
            if (err instanceof Error) {
                const cause = err.cause as { detail?: { error_code?: string }; status?: number } | undefined;
                const errorCode = cause?.detail?.error_code;

                if (errorCode === "INVALID_CREDENTIALS") {
                    setError(t("auth.errors.invalidCredentials"));
                } else if (cause?.status && cause.status >= 500) {
                    setError(t("auth.errors.serverError"));
                } else {
                    setError(t("auth.errors.serverError"));
                }
            } else {
                setError(t("auth.errors.serverError"));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        login();
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    bgcolor: "#f0f0f0",
                }}
            >
                <LocaleSelector style={{ position: "absolute", top: "16px", right: "16px" }} />
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: "450px",
                        maxWidth: "530px",
                    }}
                >
                    <Logo />
                    <Typography
                        variant="h5"
                        component="h1"
                        align="center"
                        tx={
                            isCada
                                ? "auth.title.cada"
                                : isATD
                                  ? "auth.title.atd"
                                  : "auth.title.nada"
                        }
                    />

                    <Typography
                        variant="h6"
                        component="h2"
                        fontWeight={500}
                        tx="auth.login.heading"
                        sx={{ my: 3, color: "black" }}
                    />

                    <Typography
                        variant="subtitle1"
                        sx={{ mb: 3, textAlign: "center", color: "#1C1B1B" }}
                        tx="auth.login.instructions"
                    />

                    <TextField
                        label={t("auth.login.emailLabel")}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mb: 2, backgroundColor: "white" }}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <TextField
                        label={t("auth.login.passwordLabel")}
                        type={showPassword ? "text" : "password"}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label={
                                                showPassword
                                                    ? "hide the password"
                                                    : "display the password"
                                            }
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            onMouseUp={handleMouseUpPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 1, backgroundColor: "white" }}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <Box sx={{ width: "100%", textAlign: "left", pl: "14px" }}>
                        <Link
                            href="/forgot-password"
                            sx={{
                                color: "#666666",
                                textTransform: "none",
                                fontSize: 12,
                                textDecoration: "none",
                            }}
                        >
                            {t("auth.login.forgotPasswordLink")}
                        </Link>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1, color: "red", textAlign: "center" }}>
                        {error}
                    </Typography>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!(email && password) || isLoading}
                        sx={{
                            color: email && password && !isLoading ? "white" : "black",
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                            t("auth.login.signInButton")
                        )}
                    </Button>
                </Box>
            </Box>
            <LicenseAgreement onAgree={acceptTerms} onCancel={cancelTerms} open={showTermsDialog} />
        </>
    );
};

export default LoginPage;
